/**
 * Directory-aware drop handling (spec 2026-08-16-04 §3).
 *
 * A client depositing a folder organized it themselves, and that organization
 * is provenance worth keeping: every file dropped inside a directory is sent
 * with the relative path it had in the depositor's tree (filename included),
 * which the backend freezes on `source_details.original_path`.
 *
 * A plain single-file drop carries NO path — the bare filename adds nothing
 * over `file_name`, so `originalPath` stays undefined and the upload sends
 * nothing.
 */

export interface DroppedFile {
    file: File
    /**
     * Relative path inside the dropped directory, filename included and using
     * "/" separators (e.g. "Chaufferie/contrats/P2-2023.pdf"). Undefined when
     * the file was dropped/picked on its own.
     */
    originalPath?: string
}

/** Hard caps so a pathological deposit cannot hang the tab. */
const MAX_FILES = 5000
const MAX_DEPTH = 32

// Minimal structural types for the non-standard File and Directory Entries
// API — TS's DOM lib only ships `FileSystemEntry` without the reader shape we
// need, and we never touch anything beyond these members.
interface FsEntryLike {
    isFile: boolean
    isDirectory: boolean
    name: string
    file?: (cb: (file: File) => void, err?: (e: unknown) => void) => void
    createReader?: () => { readEntries: (cb: (entries: FsEntryLike[]) => void, err?: (e: unknown) => void) => void }
}

function entryFile(entry: FsEntryLike): Promise<File | null> {
    return new Promise(resolve => {
        if (!entry.file) {
            resolve(null)
            return
        }
        entry.file(f => resolve(f), () => resolve(null))
    })
}

/** readEntries only returns a batch at a time — loop until it comes back empty. */
async function readAllEntries(entry: FsEntryLike): Promise<FsEntryLike[]> {
    const reader = entry.createReader?.()
    if (!reader) return []

    const all: FsEntryLike[] = []
    for (;;) {
        const batch = await new Promise<FsEntryLike[]>(resolve => {
            reader.readEntries(entries => resolve(entries), () => resolve([]))
        })
        if (batch.length === 0) break
        all.push(...batch)
        if (all.length >= MAX_FILES) break
    }
    return all
}

async function walkEntry(
    entry: FsEntryLike,
    prefix: string,
    depth: number,
    out: DroppedFile[],
): Promise<void> {
    if (out.length >= MAX_FILES) return

    if (entry.isFile) {
        const file = await entryFile(entry)
        if (file) {
            out.push(prefix ? { file, originalPath: `${prefix}${entry.name}` } : { file })
        }
        return
    }

    if (!entry.isDirectory || depth >= MAX_DEPTH) return

    const children = await readAllEntries(entry)
    for (const child of children) {
        await walkEntry(child, `${prefix}${entry.name}/`, depth + 1, out)
    }
}

/**
 * Turns a drop into the files it carries, resolving dropped directories into
 * their contents. Top-level plain files get no `originalPath`; anything found
 * inside a directory gets its path relative to the drop.
 *
 * Falls back to `dataTransfer.files` when the browser has no
 * `webkitGetAsEntry` (directories are simply not expandable there).
 */
export async function collectDroppedFiles(dataTransfer: DataTransfer): Promise<DroppedFile[]> {
    const items = dataTransfer.items
    const entries: FsEntryLike[] = []

    if (items && items.length > 0) {
        for (let i = 0; i < items.length; i++) {
            const item = items[i]
            if (item.kind !== 'file') continue
            const entry = item.webkitGetAsEntry?.() as FsEntryLike | null
            if (entry) entries.push(entry)
        }
    }

    if (entries.length === 0) {
        return filesFromFileList(dataTransfer.files)
    }

    const collected: DroppedFile[] = []
    for (const entry of entries) {
        await walkEntry(entry, '', 0, collected)
    }
    return collected
}

/**
 * Maps a FileList (a `<input type="file">` selection, or a drop on a browser
 * without the entries API) to DroppedFile, honoring `webkitRelativePath` so a
 * directory picker keeps its paths too.
 */
export function filesFromFileList(files: FileList | File[] | null | undefined): DroppedFile[] {
    if (!files) return []
    return Array.from(files).slice(0, MAX_FILES).map(file => {
        const relative = (file as File & { webkitRelativePath?: string }).webkitRelativePath
        return relative && relative.includes('/') ? { file, originalPath: relative } : { file }
    })
}

/**
 * Appends `original_path` to an upload FormData when the file came from a
 * folder deposit. Single files append nothing at all — an empty string would
 * store a meaningless key on frozen provenance.
 */
export function appendOriginalPath(formData: FormData, originalPath?: string): void {
    const trimmed = originalPath?.trim()
    if (trimmed) formData.append('original_path', trimmed)
}
